import React from "react";
import { connect } from "react-redux";
import { Row, Col } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import {
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import classNames from "classnames";
import get from "lodash.get";
import isEqual from "lodash.isequal";
import {
  IPost,
  PostType,
  NotificationType,
} from "../../../types/services/IPost";
import { isCurrentUserCollaborator } from "../../../utils/roleCheck";
import s from "./styles.scss";
import ChoiceInput from "../choiceInput";

const titles = {
  allNotifications: "Sharing Hierarchy",
  email: "Email",
  sms: "Text",
};

type ChannelType = "sms" | "email";

type NotificationElement = {
  type: ChannelType;
  checked: boolean;
  disabled: boolean;
};

interface Props extends ServiceCallerProps {
  currentPost?: {
    post: IPost;
    type: PostType;
  };
  colSize: number;

  emailPostNotifications: boolean;
  emailPostNotificationsDefault: boolean;
  smsPostNotifications: boolean;
  smsPostNotificationsDefault: boolean;
  isVisible: boolean;

  canEditPublishing: boolean;
  busyLoading: boolean;
  isNew: boolean;
  updateSharingHierarchy: (options: {
    sms: boolean;
    email: boolean;
    push: boolean;
  }) => void;
}

interface State {
  allNotifications: boolean;
  notifications: {
    sms: boolean;
    email: boolean;
  };
}

class SharingHierarchySection extends React.PureComponent<Props, State> {
  static defaultProps = {
    isVisible: true,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      allNotifications: this.initialAllNotificationsChecked(
        props.smsPostNotificationsDefault,
        props.emailPostNotificationsDefault
      ),
      notifications: {
        sms: props.smsPostNotificationsDefault,
        email: props.emailPostNotificationsDefault,
      },
    };
  }

  initialAllNotificationsChecked = (sms: boolean, email: boolean) => {
    return sms && email;
  };

  componentDidMount() {
    this.reloadNotifications();
  }

  componentDidUpdate(prevProps: Props) {
    const updateStateNotifications = (notificationObject: Partial<State["notifications"]>) => {
      this.setState(state => {
        const notificationPreferences = {
          ...state.notifications,
          ...notificationObject,
        };

        this.emitNotifications(notificationPreferences);

        return {
          allNotifications: this.getAllNotificationsValue(
            notificationPreferences.sms,
            notificationPreferences.email
          ),
          notifications: notificationPreferences,
        };
      });
    };

    if (
      prevProps.emailPostNotificationsDefault !==
        this.props.emailPostNotificationsDefault &&
      this.props.isNew
    ) {
      updateStateNotifications({
        email: this.props.emailPostNotificationsDefault,
      });
    }

    if (
      prevProps.smsPostNotificationsDefault !==
        this.props.smsPostNotificationsDefault &&
      this.props.isNew
    ) {
      updateStateNotifications({ sms: this.props.smsPostNotificationsDefault });
    }

    if (!this.props.isNew && prevProps.isNew !== this.props.isNew) {
      updateStateNotifications({
        sms: get(this.props.currentPost, "post.notifications.sms", false),
        email: get(this.props.currentPost, "post.notifications.email", false),
      });
    }

    if (
      !isEqual(prevProps.emailPostNotifications, this.props.emailPostNotifications) ||
      !isEqual(prevProps.smsPostNotifications, this.props.smsPostNotifications)
    ) {
      this.reloadNotifications();
    }
  }

  emitNotifications = (notifications: State["notifications"]) => {
    this.props.updateSharingHierarchy({
      ...notifications,
      push: false,
    });
  };

  recalculateAllNotification = (checkedName: ChannelType, checkedValue: boolean) => {
    this.setState(
      {
        allNotifications:
          (checkedName === "sms" ? checkedValue : this.state.notifications.sms) &&
          (checkedName === "email"
            ? checkedValue
            : this.state.notifications.email),
      },
      () => {
        this.emitNotifications({
          ...this.state.notifications,
          [checkedName]: checkedValue,
        });
      }
    );
  };

  allNotificationsChanged = event => {
    const checked = event.target.checked;
    const options = {
      sms: checked && this.props.smsPostNotifications,
      email: checked && this.props.emailPostNotifications,
    };

    this.setState(
      {
        allNotifications: this.getAllNotificationsValue(
          options.sms,
          options.email
        ),
        notifications: { ...options },
      },
      () => {
        this.emitNotifications(options);
      }
    );
  };

  reloadNotifications = () => {
    const { smsPostNotifications, emailPostNotifications } = this.props;
    const { sms, email } = this.state.notifications;

    const options = {
      sms: sms && smsPostNotifications,
      email: email && emailPostNotifications,
    };

    const allSelected =
      (options.sms || !smsPostNotifications) &&
      (options.email || !emailPostNotifications);

    this.setState(
      {
        allNotifications: allSelected,
        notifications: { ...options },
      },
      () => {
        this.emitNotifications(options);
      }
    );
  };

  getAllNotificationsValue(
    sms: boolean,
    email: boolean,
    useConfig: boolean = this.props.isNew ||
      this.props.currentPost?.post?.status === "draft"
  ): boolean {
    return (
      (sms || (useConfig && !this.props.smsPostNotifications)) &&
      (email || (useConfig && !this.props.emailPostNotifications))
    );
  }

  onCheckedChanged = (checkedName: ChannelType) => {
    return event => {
      const checkedValue = event.target.checked;
      this.setState(
        {
          notifications: {
            ...this.state.notifications,
            [checkedName]: checkedValue,
          },
        },
        () => {
          this.recalculateAllNotification(checkedName, checkedValue);
        }
      );
    };
  };

  allNotificationsChecked = () => {
    return (
      this.state.allNotifications &&
      (this.props.smsPostNotifications || this.props.emailPostNotifications)
    );
  };

  renderCheckInput = (
    type: NotificationType,
    disabled: boolean,
    checked: boolean,
    onChangeAction: any,
    index: number
  ): JSX.Element => {
    const { canEditPublishing, busyLoading } = this.props;
    const allSection = type == "allNotifications";
    const checkId = type + (checked ? "_checked" : "_unchecked");

    return (
      <Row key={type + index}>
        <ChoiceInput
          colClassName={
            !allSection
              ? classNames(s.choice, disabled && s.disabledCheck)
              : null
          }
          colSize={!allSection ? { offset: 1, size: 12 } : 12}
          title={titles[type]}
          checked={checked}
          className={
            allSection
              ? s.titleLabel
              : classNames(s.label, disabled ? s.disabledText : s.enableText)
          }
          onChangeHandler={onChangeAction}
          id={checkId}
          isDisabled={
            !canEditPublishing ||
            busyLoading != null ||
            !isCurrentUserCollaborator(this.props.currentCharity) ||
            disabled
          }
        />
      </Row>
    );
  };

  render() {
    if (!this.props.isVisible) return null;

    const {
      colSize,
      smsPostNotifications,
      emailPostNotifications,
      isNew,
    } = this.props;

    const { sms, email } = this.state.notifications;
    const smsChecked = (!isNew || smsPostNotifications) && sms;
    const emailChecked = (!isNew || emailPostNotifications) && email;

    const shareOptions: Array<NotificationElement> = [
      {
        type: "sms",
        checked: smsChecked,
        disabled: !smsPostNotifications,
      },
      {
        type: "email",
        checked: emailChecked,
        disabled: !emailPostNotifications,
      },
    ];

    return (
      <Col sm={colSize}>
        {this.renderCheckInput(
          "allNotifications",
          !smsPostNotifications && !emailPostNotifications,
          this.allNotificationsChecked(),
          this.allNotificationsChanged,
          shareOptions.length
        )}

        {shareOptions.map((option, index) => {
          return this.renderCheckInput(
            option.type,
            option.disabled,
            option.checked,
            this.onCheckedChanged(option.type),
            index
          );
        })}
      </Col>
    );
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const smsPostNotifications =
    get(
      firebase,
      `data.${user.currentCharity.id}.config.smsPostNotifications`
    ) !== false;
  const emailPostNotifications =
    get(
      firebase,
      `data.${user.currentCharity.id}.config.emailPostNotifications`
    ) !== false;
  const smsPostNotificationsDefault =
    get(
      firebase,
      `data.${user.currentCharity.id}.config.smsPostNotificationsDefault`
    ) !== false;
  const emailPostNotificationsDefault =
    get(
      firebase,
      `data.${user.currentCharity.id}.config.emailPostNotificationsDefault`
    ) !== false;

  return {
    emailPostNotifications,
    smsPostNotifications,
    smsPostNotificationsDefault,
    emailPostNotificationsDefault,
  };
};

export default serviceCaller(
  withModals(connect(mapStateToProps)(withStyles(s)(SharingHierarchySection)))
);
